import { useLazyQuery, gql } from '@apollo/client';
import { formatDateShort } from '../utils/dateFormat';

// ==================== TYPES ====================

export interface SalesReportDailyBreakdown {
  date: string;
  totalSales: number;
  invoiceCount: number;
  electronicCount: number;
  internalCount: number;
}

export interface SalesReport {
  totalSales: number;
  totalInvoices: number;
  electronicInvoices: number;
  electronicAmount: number;
  internalInvoices: number;
  internalAmount: number;
  averageTicket: number;
  dailyBreakdown: SalesReportDailyBreakdown[];
}

export interface DeletionsByDay {
  date: string;
  count: number;
  amount: number;
}

export interface DeletionsByUser {
  userId: string;
  username: string;
  count: number;
  amount: number;
}

export interface DeletionsReport {
  totalDeleted: number;
  totalAmount: number;
  automaticDeletions: number;
  manualDeletions: number;
  deletionsByDay: DeletionsByDay[];
  deletionsByUser: DeletionsByUser[];
}

export interface TargetDailyDetail {
  date: string;
  target: number;
  actual: number;
  completion: number;
  difference: number;
}

export interface TargetsReport {
  daysAnalyzed: number;
  daysMetTarget: number;
  daysBelowTarget: number;
  averageCompletion: number;
  totalTarget: number;
  totalActual: number;
  dailyDetails: TargetDailyDetail[];
}

export interface HourlyBreakdown {
  hour: number;
  sales: number;
  invoiceCount: number;
  percentage: number;
}

export interface HourlyReport {
  date: string;
  totalSales: number;
  hourlyBreakdown: HourlyBreakdown[];
  peakHour: number;
  peakSales: number;
  lowHour: number;
  lowSales: number;
}

export interface AuditActionsByModule {
  module: string;
  count: number;
}

export interface AuditActionsByUser {
  userId: string;
  username: string;
  count: number;
}

export interface AuditActionsByType {
  action: string;
  count: number;
}

export interface AuditRecentAction {
  id: string;
  action: string;
  module: string;
  userId: string;
  details: string;
  createdAt: string;
}

export interface AuditReport {
  totalActions: number;
  actionsByModule: AuditActionsByModule[];
  actionsByUser: AuditActionsByUser[];
  actionsByType: AuditActionsByType[];
  recentActions: AuditRecentAction[];
}

export interface TaxDailyBreakdown {
  date: string;
  subtotal: number;
  tax: number;
  total: number;
}

export interface TaxReport {
  totalTaxCollected: number;
  totalSubtotal: number;
  effectiveTaxRate: number;
  dailyBreakdown: TaxDailyBreakdown[];
}

export interface ExecutiveSummary {
  period: {
    start: string;
    end: string;
    days: number;
  };
  sales: {
    total: number;
    invoiceCount: number;
    averageDaily: number;
    averageTicket: number;
  };
  deletions: {
    count: number;
    amount: number;
    percentage: number;
  };
  targets: {
    averageCompletion: number;
    daysMetTarget: number;
    daysTotal: number;
  };
  taxes: {
    collected: number;
    effectiveRate: number;
  };
}

// ==================== QUERIES ====================

const GET_SALES_REPORT = gql`
  query GetSalesReport($startDate: DateTime!, $endDate: DateTime!) {
    getSalesReport(startDate: $startDate, endDate: $endDate) {
      totalSales
      totalInvoices
      electronicInvoices
      electronicAmount
      internalInvoices
      internalAmount
      averageTicket
      dailyBreakdown {
        date
        totalSales
        invoiceCount
        electronicCount
        internalCount
      }
    }
  }
`;

const GET_DELETIONS_REPORT = gql`
  query GetDeletionsReport($startDate: DateTime!, $endDate: DateTime!) {
    getDeletionsReport(startDate: $startDate, endDate: $endDate) {
      totalDeleted
      totalAmount
      automaticDeletions
      manualDeletions
      deletionsByDay {
        date
        count
        amount
      }
      deletionsByUser {
        userId
        username
        count
        amount
      }
    }
  }
`;

const GET_TARGETS_REPORT = gql`
  query GetTargetsReport($startDate: DateTime!, $endDate: DateTime!) {
    getTargetsReport(startDate: $startDate, endDate: $endDate) {
      daysAnalyzed
      daysMetTarget
      daysBelowTarget
      averageCompletion
      totalTarget
      totalActual
      dailyDetails {
        date
        target
        actual
        completion
        difference
      }
    }
  }
`;

const GET_HOURLY_REPORT = gql`
  query GetHourlyReport($date: DateTime!) {
    getHourlyReport(date: $date) {
      date
      totalSales
      hourlyBreakdown {
        hour
        sales
        invoiceCount
        percentage
      }
      peakHour
      peakSales
      lowHour
      lowSales
    }
  }
`;

const GET_AUDIT_REPORT = gql`
  query GetAuditReport($startDate: DateTime!, $endDate: DateTime!, $options: AuditReportOptions) {
    getAuditReport(startDate: $startDate, endDate: $endDate, options: $options) {
      totalActions
      actionsByModule {
        module
        count
      }
      actionsByUser {
        userId
        username
        count
      }
      actionsByType {
        action
        count
      }
      recentActions {
        id
        action
        module
        userId
        details
        createdAt
      }
    }
  }
`;

const GET_TAX_REPORT = gql`
  query GetTaxReport($startDate: DateTime!, $endDate: DateTime!) {
    getTaxReport(startDate: $startDate, endDate: $endDate) {
      totalTaxCollected
      totalSubtotal
      effectiveTaxRate
      dailyBreakdown {
        date
        subtotal
        tax
        total
      }
    }
  }
`;

const GET_EXECUTIVE_SUMMARY = gql`
  query GetExecutiveSummary($startDate: DateTime!, $endDate: DateTime!) {
    getExecutiveSummary(startDate: $startDate, endDate: $endDate) {
      period {
        start
        end
        days
      }
      sales {
        total
        invoiceCount
        averageDaily
        averageTicket
      }
      deletions {
        count
        amount
        percentage
      }
      targets {
        averageCompletion
        daysMetTarget
        daysTotal
      }
      taxes {
        collected
        effectiveRate
      }
    }
  }
`;

// ==================== HOOKS ====================

/**
 * Hook para obtener reporte de ventas (lazy query)
 */
export const useSalesReport = () => {
  const [fetch, { data, loading, error }] = useLazyQuery(GET_SALES_REPORT, {
    fetchPolicy: 'network-only',
  });

  const fetchReport = async (startDate: Date, endDate: Date) => {
    const result = await fetch({
      variables: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      },
    });
    return result.data?.getSalesReport as SalesReport | undefined;
  };

  return {
    fetchReport,
    report: data?.getSalesReport as SalesReport | undefined,
    loading,
    error,
  };
};

/**
 * Hook para obtener reporte de eliminaciones (lazy query)
 */
export const useDeletionsReport = () => {
  const [fetch, { data, loading, error }] = useLazyQuery(GET_DELETIONS_REPORT, {
    fetchPolicy: 'network-only',
  });

  const fetchReport = async (startDate: Date, endDate: Date) => {
    const result = await fetch({
      variables: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      },
    });
    return result.data?.getDeletionsReport as DeletionsReport | undefined;
  };

  return {
    fetchReport,
    report: data?.getDeletionsReport as DeletionsReport | undefined,
    loading,
    error,
  };
};

/**
 * Hook para obtener reporte de metas (lazy query)
 */
export const useTargetsReport = () => {
  const [fetch, { data, loading, error }] = useLazyQuery(GET_TARGETS_REPORT, {
    fetchPolicy: 'network-only',
  });

  const fetchReport = async (startDate: Date, endDate: Date) => {
    const result = await fetch({
      variables: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      },
    });
    return result.data?.getTargetsReport as TargetsReport | undefined;
  };

  return {
    fetchReport,
    report: data?.getTargetsReport as TargetsReport | undefined,
    loading,
    error,
  };
};

/**
 * Hook para obtener reporte por hora (lazy query)
 */
export const useHourlyReport = () => {
  const [fetch, { data, loading, error }] = useLazyQuery(GET_HOURLY_REPORT, {
    fetchPolicy: 'network-only',
  });

  const fetchReport = async (date: Date) => {
    const result = await fetch({
      variables: {
        date: date.toISOString(),
      },
    });
    return result.data?.getHourlyReport as HourlyReport | undefined;
  };

  return {
    fetchReport,
    report: data?.getHourlyReport as HourlyReport | undefined,
    loading,
    error,
  };
};

/**
 * Hook para obtener reporte de auditoría (lazy query)
 */
export const useAuditReport = () => {
  const [fetch, { data, loading, error }] = useLazyQuery(GET_AUDIT_REPORT, {
    fetchPolicy: 'network-only',
  });

  const fetchReport = async (startDate: Date, endDate: Date, options?: {
    userId?: string;
    module?: string;
    action?: string;
  }) => {
    const result = await fetch({
      variables: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        options,
      },
    });
    return result.data?.getAuditReport as AuditReport | undefined;
  };

  return {
    fetchReport,
    report: data?.getAuditReport as AuditReport | undefined,
    loading,
    error,
  };
};

/**
 * Hook para obtener reporte de impuestos (lazy query)
 */
export const useTaxReport = () => {
  const [fetch, { data, loading, error }] = useLazyQuery(GET_TAX_REPORT, {
    fetchPolicy: 'network-only',
  });

  const fetchReport = async (startDate: Date, endDate: Date) => {
    const result = await fetch({
      variables: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      },
    });
    return result.data?.getTaxReport as TaxReport | undefined;
  };

  return {
    fetchReport,
    report: data?.getTaxReport as TaxReport | undefined,
    loading,
    error,
  };
};

/**
 * Hook para obtener resumen ejecutivo (lazy query)
 */
export const useExecutiveSummary = () => {
  const [fetch, { data, loading, error }] = useLazyQuery(GET_EXECUTIVE_SUMMARY, {
    fetchPolicy: 'network-only',
  });

  const fetchReport = async (startDate: Date, endDate: Date) => {
    const result = await fetch({
      variables: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      },
    });
    return result.data?.getExecutiveSummary as ExecutiveSummary | undefined;
  };

  return {
    fetchReport,
    report: data?.getExecutiveSummary as ExecutiveSummary | undefined,
    loading,
    error,
  };
};

// ==================== HELPERS ====================

/**
 * Formatear moneda
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Formatear porcentaje
 */
export const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

/**
 * Formatear fecha para mostrar
 */
export const formatDate = (dateStr: string): string => {
  return formatDateShort(dateStr);
};

/**
 * Formatear hora
 */
export const formatHour = (hour: number): string => {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:00 ${period}`;
};

/**
 * Obtener rango de fechas predefinido
 */
export const getDateRange = (preset: 'today' | 'week' | 'month' | 'quarter' | 'year'): { start: Date; end: Date } => {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  const start = new Date(now);
  start.setHours(0, 0, 0, 0);

  switch (preset) {
    case 'today':
      // Ya está configurado
      break;
    case 'week':
      start.setDate(start.getDate() - 6);
      break;
    case 'month':
      start.setDate(1);
      break;
    case 'quarter':
      start.setMonth(Math.floor(start.getMonth() / 3) * 3, 1);
      break;
    case 'year':
      start.setMonth(0, 1);
      break;
  }

  return { start, end };
};
